import React, { useMemo } from 'react';
import { View, Text, Pressable, Platform } from 'react-native';
import { Clock, X } from 'lucide-react-native';
import { useTheme } from '../../../theme/useTheme';
import { WEB_HOVER_TRANSITION_STYLE } from '../../../shared/constants/animations';
import { getLateJoinBannerStyles } from './LateJoinBanner.styles';
import { LATE_JOIN_BANNER_COPY, LATE_JOIN_BANNER_TEST_IDS } from './LateJoinBanner.constants';

interface LateJoinBannerProps {
  /** When provided, renders a dismiss (×) button that calls this. */
  onDismiss?: () => void;
}

/**
 * One-time notice shown to a player who joined after the tournament started: matches played before
 * they joined are forfeited (0 points). Derived from the match feed — see `worldcupLateJoin.helper`.
 */
export const LateJoinBanner: React.FC<LateJoinBannerProps> = ({ onDismiss }) => {
  const theme = useTheme();
  const styles = useMemo(() => getLateJoinBannerStyles(theme), [theme]);

  return (
    <View style={styles.container} testID={LATE_JOIN_BANNER_TEST_IDS.ROOT}>
      <View style={styles.iconWrap}>
        <Clock size={16} color={theme.accent} strokeWidth={2.5} />
      </View>
      <View style={styles.textWrap}>
        <Text style={styles.title}>{LATE_JOIN_BANNER_COPY.TITLE}</Text>
        <Text style={styles.body}>{LATE_JOIN_BANNER_COPY.BODY}</Text>
      </View>
      {onDismiss != null && (
        <Pressable
          onPress={onDismiss}
          hitSlop={8}
          accessibilityLabel="Dismiss"
          testID={LATE_JOIN_BANNER_TEST_IDS.DISMISS}
          style={({ pressed, hovered }) => [
            styles.dismissButton,
            Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
            hovered && Platform.OS === 'web' && styles.dismissButtonHover,
            pressed && styles.dismissButtonHover,
          ]}
        >
          <X size={16} color={theme.gray[500]} strokeWidth={2.5} />
        </Pressable>
      )}
    </View>
  );
};
